// src/components/crm/JobTable.js
import React from 'react';
import { ArrowUp, ArrowDown, Edit, Trash2, Calendar, User, MapPin, CheckSquare, Square, CheckCircle2 } from 'lucide-react';
import JobStatusBadge from '../common/JobStatusBadge';

const JobTable = ({
  jobs,
  openModal,
  onDeleteRequest,
  requestSort,
  sortConfig,
  // Batch selection props
  selectedJobIds = new Set(),
  onSelectJob,
  onSelectAll,
  batchMode = false,
}) => {

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
    return sortConfig.direction === 'ascending'
      ? <ArrowUp className="inline-block ml-1 h-4 w-4 text-blue-600" />
      : <ArrowDown className="inline-block ml-1 h-4 w-4 text-blue-600" />;
  };

  // r1-rec-jobtable-display-a1f6d3b8
  // Accept both a Firestore Timestamp (real jobs) and a JS Date (projected
  // recurring rows carry startAt as a Date). Previously a bare ?.toDate check
  // returned 'N/A' for every projected occurrence.
  const formatDate = (timestamp) => {
    const d = timestamp?.toDate ? timestamp.toDate()
            : (timestamp instanceof Date ? timestamp : null);
    if (!d) return 'N/A';
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  // Only selectable jobs: non-Canceled
  const selectableJobs = jobs.filter(j => j.status !== 'Canceled');
  const allSelected = selectableJobs.length > 0 && selectableJobs.every(j => selectedJobIds.has(j.id));
  const someSelected = selectableJobs.some(j => selectedJobIds.has(j.id));

  return (
    <div className="mt-4 sm:mt-6">

      {/* ─── DESKTOP VIEW ─────────────────────────────────────────────────────── */}
      <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
            <tr>
              {batchMode && (
                <th className="px-4 py-3 w-10">
                  <button
                    onClick={() => onSelectAll?.(allSelected ? [] : selectableJobs.map(j => j.id))}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title={allSelected ? "Deselect all" : "Select all"}
                  >
                    {allSelected
                      ? <CheckSquare size={16} className="text-blue-600" />
                      : someSelected
                        ? <CheckSquare size={16} className="text-blue-400" />
                        : <Square size={16} />}
                  </button>
                </th>
              )}
              <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('jobNumber')}>Job #{getSortIndicator('jobNumber')}</th>
              <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('title')}>Title{getSortIndicator('title')}</th>
              <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('customerName')}>Customer{getSortIndicator('customerName')}</th>
              <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('startAt')}>Start Time{getSortIndicator('startAt')}</th>
              <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('assignedToNames')}>Assigned{getSortIndicator('assignedToNames')}</th>
              <th className="px-6 py-3 text-center cursor-pointer hover:bg-gray-100" onClick={() => requestSort('status')}>Status{getSortIndicator('status')}</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {jobs.map(job => {
              const isSelected = selectedJobIds.has(job.id);
              const isSelectable = job.status !== 'Canceled';
              return (
                <tr
                  key={job.id}
                  className={`transition-colors cursor-pointer ${isSelected ? 'bg-blue-50' : 'bg-white hover:bg-blue-50'}`}
                  onClick={() => {
                    if (batchMode && isSelectable) {
                      onSelectJob?.(job.id, !isSelected);
                    } else {
                      openModal(job);
                    }
                  }}
                >
                  {batchMode && (
                    <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                      {isSelectable ? (
                        <button
                          onClick={() => onSelectJob?.(job.id, !isSelected)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          {isSelected
                            ? <CheckSquare size={16} className="text-blue-600" />
                            : <Square size={16} />}
                        </button>
                      ) : (
                        <span className="w-4 inline-block" />
                      )}
                    </td>
                  )}
                  {/* r1-rec-jobtable-display-a1f6d3b8 */}
                  <td className="px-6 py-4 font-medium text-gray-900">
                    {job.isProjected ? (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">Recurring</span>
                    ) : (
                      `#${job.jobNumber || '-'}`
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium">{job.title}</td>
                  <td className="px-6 py-4">{job.customerDetails?.name || job.clientName || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(job.startAt)}</td>
                  <td className="px-6 py-4">
                    <div className="flex -space-x-2 overflow-hidden">
                      {(job.assignedToNames || []).map((name, i) => (
                        <span key={i} className="inline-flex items-center justify-center h-6 w-6 rounded-full ring-2 ring-white bg-gray-200 text-xs font-medium text-gray-600" title={name}>
                          {name.charAt(0)}
                        </span>
                      ))}
                      {(!job.assignedToNames?.length) && <span className="text-gray-400 italic text-xs">Unassigned</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center"><JobStatusBadge status={job.status} /></td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openModal(job)} className="p-1.5 hover:bg-blue-100 text-blue-600 rounded"><Edit size={16} /></button>
                      <button onClick={() => onDeleteRequest(job.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {jobs.length === 0 && (
          <div className="text-center py-10 text-gray-500">No jobs found.</div>
        )}
      </div>

      {/* ─── MOBILE CARD VIEW ─────────────────────────────────────────────────── */}
      <div className="md:hidden space-y-3 pb-20">
        {jobs.map(job => {
          const isSelected = selectedJobIds.has(job.id);
          const isSelectable = job.status !== 'Canceled';
          return (
            <div
              key={job.id}
              onClick={() => {
                if (batchMode && isSelectable) onSelectJob?.(job.id, !isSelected);
                else openModal(job);
              }}
              className={`p-4 rounded-xl shadow-sm border active:scale-[0.99] transition-all cursor-pointer relative
                ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}
            >
              {batchMode && isSelectable && (
                <div className="absolute top-4 left-4">
                  {isSelected
                    ? <CheckSquare size={18} className="text-blue-600" />
                    : <Square size={18} className="text-gray-400" />}
                </div>
              )}

              <div className="absolute top-4 right-4">
                <JobStatusBadge status={job.status} />
              </div>

              <div className={`pr-24 mb-2 ${batchMode ? 'pl-7' : ''}`}>
                {/* r1-rec-jobtable-display-a1f6d3b8 */}
                {job.isProjected ? (
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">Recurring</span>
                ) : (
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Job #{job.jobNumber || '---'}</span>
                )}
                <h3 className="text-lg font-bold text-gray-900 leading-tight">{job.title}</h3>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center text-sm text-gray-600">
                  <User size={14} className="mr-2 text-gray-400" />
                  <span className="truncate">{job.customerDetails?.name || job.clientName || 'Unknown'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar size={14} className="mr-2 text-gray-400" />
                  <span>{formatDate(job.startAt)}</span>
                </div>
                {job.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin size={14} className="mr-2 text-gray-400" />
                    <span className="truncate">{job.location.address || 'No Address'}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                <div className="flex -space-x-2">
                  {(job.assignedToNames || []).slice(0, 3).map((name, i) => (
                    <span key={i} className="h-7 w-7 rounded-full ring-2 ring-white bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">
                      {name.charAt(0)}
                    </span>
                  ))}
                  {job.assignedToNames?.length > 3 && (
                    <span className="h-7 w-7 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                      +{job.assignedToNames.length - 3}
                    </span>
                  )}
                  {(!job.assignedToNames?.length) && <span className="text-xs text-gray-400 italic">Unassigned</span>}
                </div>
                {!batchMode && (
                  <button
                    onClick={e => { e.stopPropagation(); onDeleteRequest(job.id); }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {jobs.length === 0 && (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500">No jobs found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobTable;